import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Search, ChevronDown, Package, Truck, CheckCircle2, Ban, RotateCcw, Clock, Copy, Check, PackageCheck,
} from "lucide-react";
import type { AppDispatch, RootState } from "../../redux/store";
import { listenToUserOrders, cancelOrder, markOrderDelivered, Order } from "../../redux/ordersSlice";

/* Buyer view of orders: filters, search, expandable cards, a delivery tracker,
   "Mark as received" once the seller has shipped, and "Cancel" while it hasn't.
   Data comes only from ordersSlice (no direct Firebase calls here). */

/* ═══════════════════════════ STYLES: black, white and red only (scoped to .co-page) ═══════════════════════════ */
const INK = "#000";
const PAPER = "#fff";
const RED = "#E50914";
const RED_DEEP = "#A8060E";
const SOFT = "#F5F5F5";
const RULE = "#E6E6E6";
const MUTE = "#6E6E6E";

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,500;0,9..144,700;1,9..144,500;1,9..144,700&family=DM+Sans:wght@400;500;600;700&display=swap');
.co-page, .co-page *, .co-page *::before, .co-page *::after{ box-sizing:border-box; font-family:'DM Sans',system-ui,sans-serif; }
.co-page{ position:relative; min-height:100vh; width:100%; max-width:100vw; overflow-x:hidden; color:${INK}; background:${PAPER};
  background-image:radial-gradient(${RULE} 1.4px, transparent 1.6px); background-size:22px 22px;
  padding:22px 14px 90px; padding-top:calc(22px + env(safe-area-inset-top)); padding-bottom:calc(90px + env(safe-area-inset-bottom)); }
.co-page h1, .co-page h2, .co-page h3, .co-page .co-serif{ font-family:'Fraunces',Georgia,serif; }
.co-page p{ margin:0; }
.co-page button{ font-family:inherit; cursor:pointer; border:none; background:none; color:inherit; padding:0; }
.co-page button:disabled{ cursor:not-allowed; opacity:.5; }
.co-page button:focus-visible, .co-page input:focus-visible{ outline:2px solid ${RED}; outline-offset:3px; }

.co-wrap{ position:relative; z-index:1; width:100%; max-width:720px; min-width:0; margin:0 auto; display:flex; flex-direction:column; gap:14px; }
.co-wrap > *{ min-width:0; }
.co-muted{ color:${MUTE}; }

/* top bar + title */
.co-top{ display:flex; align-items:center; justify-content:space-between; gap:12px; padding-bottom:16px; border-bottom:2px solid ${INK}; }
.co-page .co-back{ display:inline-flex; align-items:center; gap:8px; font-size:14px; font-weight:600; padding:8px 14px 8px 10px; border-radius:999px; border:1.5px solid ${INK}; background:${PAPER}; transition:background .2s, color .2s; }
.co-page .co-back:hover{ background:${INK}; color:${PAPER}; }
.co-mark{ font-family:'Fraunces',Georgia,serif; font-style:italic; font-weight:700; font-size:22px; letter-spacing:-.01em; }
.co-mark i{ display:inline-block; width:8px; height:8px; margin-left:3px; border-radius:50%; background:${RED}; }
.co-head1{ display:flex; align-items:flex-end; justify-content:space-between; gap:12px; flex-wrap:wrap; margin:10px 0 4px; }
.co-h1{ margin:0; font-size:clamp(44px,10vw,76px); line-height:.95; letter-spacing:-.02em; font-style:italic; font-weight:700; }
.co-tag{ display:inline-flex; align-items:center; gap:8px; height:34px; padding:0 14px; border-radius:999px; background:${INK}; color:${PAPER}; font-size:13px; font-weight:600; }
.co-tag i{ width:8px; height:8px; border-radius:50%; background:${RED}; animation:coBlink 1.6s ease-in-out infinite; }
@keyframes coBlink{ 0%,100%{ opacity:1; } 50%{ opacity:.25; } }

.co-error{ background:${PAPER}; color:${RED}; border:1.5px solid ${RED}; border-radius:14px; padding:12px 16px; font-size:14px; font-weight:600; }

/* buttons */
.co-page .co-btn{ display:inline-flex; align-items:center; justify-content:center; gap:8px; height:46px; padding:0 22px; border-radius:999px; border:2px solid ${INK};
  background:${RED}; color:${PAPER} !important; font-size:14px; font-weight:700; box-shadow:0 4px 0 ${RED_DEEP};
  transition:transform .15s, box-shadow .15s; }
.co-page .co-btn:hover:not(:disabled){ transform:translateY(-2px); box-shadow:0 6px 0 ${RED_DEEP}; }
.co-page .co-btn:active:not(:disabled){ transform:translateY(3px); box-shadow:0 1px 0 ${RED_DEEP}; }
.co-page .co-btn.ghost{ background:${PAPER}; color:${INK} !important; box-shadow:0 4px 0 ${INK}; }
.co-page .co-btn.ghost:hover:not(:disabled){ box-shadow:0 6px 0 ${INK}; }
.co-page .co-btn.ghost:active:not(:disabled){ box-shadow:0 1px 0 ${INK}; }
.co-page .co-btn.dark{ background:${INK}; box-shadow:0 4px 0 ${RED}; }
.co-page .co-btn.dark:hover:not(:disabled){ box-shadow:0 6px 0 ${RED}; }

/* search + pills */
.co-search{ display:flex; align-items:center; gap:10px; padding:0 18px; height:52px; border-radius:999px; color:${MUTE}; background:${PAPER}; border:1.5px solid ${INK}; box-shadow:0 4px 0 ${INK}; transition:box-shadow .2s, transform .2s; }
.co-search:focus-within{ box-shadow:0 4px 0 ${RED}; border-color:${RED}; color:${RED}; }
.co-search input{ flex:1; min-width:0; border:none; outline:none; background:transparent; font-size:16px; font-weight:500; color:${INK}; height:100%; }
.co-search input::placeholder{ color:#A3A3A3; }
.co-pills{ display:flex; gap:8px; overflow-x:auto; padding:4px 2px 8px; scrollbar-width:none; -webkit-overflow-scrolling:touch; }
.co-pills::-webkit-scrollbar{ display:none; }
.co-page .co-pill{ flex-shrink:0; display:inline-flex; align-items:center; gap:8px; height:40px; padding:0 14px 0 16px; border-radius:999px; white-space:nowrap;
  background:${PAPER}; border:1.5px solid ${INK}; font-size:14px; font-weight:600; color:${INK}; transition:background .2s, color .2s, border-color .2s; }
.co-page .co-pill:hover{ border-color:${RED}; color:${RED}; }
.co-page .co-pill.on{ background:${INK}; color:${PAPER}; }
.co-page .co-pill.on:hover{ border-color:${INK}; color:${PAPER}; }
.co-n{ min-width:22px; height:22px; padding:0 6px; border-radius:999px; display:inline-flex; align-items:center; justify-content:center; font-size:12px; font-weight:700; background:${SOFT}; color:${INK}; }
.co-pill.on .co-n{ background:${RED}; color:${PAPER}; }

/* empty */
.co-empty{ display:flex; flex-direction:column; align-items:center; gap:10px; text-align:center; padding:52px 22px; border:1.5px dashed ${INK}; border-radius:26px; background:${PAPER}; }
.co-empty b{ font-family:'Fraunces',Georgia,serif; font-style:italic; font-size:30px; font-weight:700; }
.co-empty span{ font-size:14px; max-width:300px; line-height:22px; }
.co-bubble{ width:84px; height:84px; border-radius:50%; display:flex; align-items:center; justify-content:center; background:${INK}; color:${PAPER}; box-shadow:0 0 0 5px ${PAPER}, 0 0 0 8px ${RED}; margin-bottom:8px; animation:coBob 2.6s ease-in-out infinite; }
@keyframes coBob{ 0%,100%{ transform:translateY(0); } 50%{ transform:translateY(-6px); } }

/* order cards */
.co-glass{ background:${PAPER}; border:1.5px solid ${INK}; border-radius:24px; }
.co-card{ overflow:hidden; min-width:0; box-shadow:0 4px 0 ${INK}; transition:box-shadow .2s, transform .2s; }
.co-card:hover{ transform:translateY(-2px); box-shadow:0 6px 0 ${INK}; }
.co-card.open{ box-shadow:5px 5px 0 ${RED}; transform:none; }
.co-card.faded{ background:${SOFT}; }
.co-card.faded .co-thumb img{ filter:grayscale(1); opacity:.6; }
.co-page .co-head{ display:flex; align-items:center; gap:14px; width:100%; padding:14px 16px; text-align:left; }
.co-thumb{ width:64px; height:64px; border-radius:18px; overflow:hidden; flex-shrink:0; display:flex; align-items:center; justify-content:center; background:${SOFT}; border:1.5px solid ${INK}; color:${MUTE}; }
.co-thumb img{ width:100%; height:100%; object-fit:cover; display:block; }
.co-main{ flex:1; min-width:0; display:flex; flex-direction:column; gap:4px; }
.co-title{ font-family:'Fraunces',Georgia,serif; font-size:20px; font-weight:700; line-height:1.15; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.co-title em{ font-family:'DM Sans',sans-serif; font-style:normal; font-size:11px; font-weight:700; color:${RED}; margin-left:8px; }
.co-sub{ display:flex; align-items:center; gap:5px; font-size:12.5px; color:${MUTE}; font-weight:500; flex-wrap:wrap; }
.co-side{ display:flex; flex-direction:column; align-items:flex-end; gap:6px; flex-shrink:0; }
.co-side > b{ font-family:'Fraunces',Georgia,serif; font-size:22px; font-weight:700; line-height:1; }
.co-chev{ color:${INK}; transition:transform .2s, color .2s; }
.co-head:hover .co-chev{ color:${RED}; }
.co-card.open .co-chev{ transform:rotate(180deg); color:${RED}; }

/* status badges: black, white, red */
.co-badge{ display:inline-flex; align-items:center; gap:5px; font-size:11.5px; font-weight:700; padding:4px 11px; border-radius:999px; white-space:nowrap; border:1.5px solid ${INK}; }
.co-pending{ background:${PAPER}; color:${INK}; border-style:dashed; }
.co-packing{ background:${RED}; border-color:${RED}; color:${PAPER}; }
.co-shipped{ background:${INK}; color:${PAPER}; }
.co-delivered{ background:${PAPER}; color:${INK}; }
.co-cancelled{ background:${SOFT}; border-color:#BDBDBD; color:${MUTE}; }
.co-refunded{ background:${PAPER}; border-color:${RED}; color:${RED}; }

.co-body{ padding:6px 16px 18px; border-top:1.5px solid ${INK}; display:flex; flex-direction:column; gap:14px; animation:coOpen .22s ease-out; }
@keyframes coOpen{ from{ opacity:0; transform:translateY(-6px); } to{ opacity:1; transform:translateY(0); } }

/* delivery tracker */
.co-track{ display:flex; align-items:flex-start; padding:20px 4px 4px; }
.co-step{ flex:1; min-width:0; display:flex; flex-direction:column; align-items:center; gap:8px; position:relative; font-size:12px; font-weight:600; color:#A3A3A3; text-align:center; }
.co-step::before{ content:""; position:absolute; top:15px; right:50%; width:100%; height:3px; border-radius:3px; background:${RULE}; z-index:0; }
.co-step:first-child::before{ display:none; }
.co-dot{ position:relative; z-index:1; width:32px; height:32px; border-radius:50%; background:${PAPER}; border:2px solid ${RULE}; display:flex; align-items:center; justify-content:center; color:${PAPER}; }
.co-step.done{ color:${INK}; }
.co-step.done::before{ background:${RED}; }
.co-step.done .co-dot{ background:${INK}; border-color:${INK}; }
.co-step.now .co-dot{ background:${RED}; border-color:${INK}; box-shadow:0 0 0 4px rgba(229,9,20,.2); animation:coPulse 1.8s ease-in-out infinite; }
@keyframes coPulse{ 0%,100%{ box-shadow:0 0 0 3px rgba(229,9,20,.25); } 50%{ box-shadow:0 0 0 7px rgba(229,9,20,.08); } }

/* items */
.co-items{ display:flex; flex-direction:column; gap:10px; }
.co-item{ display:grid; grid-template-columns:52px minmax(0,1fr) auto; grid-template-areas:"photo name total" "photo qty total"; column-gap:12px; row-gap:2px; align-items:center;
  padding:8px; border-radius:18px; background:${PAPER}; border:1.5px solid ${RULE}; }
.co-iphoto{ grid-area:photo; width:52px; height:52px; border-radius:14px; overflow:hidden; display:flex; align-items:center; justify-content:center; background:${SOFT}; border:1.5px solid ${INK}; color:${MUTE}; }
.co-iphoto img{ width:100%; height:100%; object-fit:cover; display:block; }
.co-iname{ grid-area:name; font-size:15px; font-weight:600; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; min-width:0; }
.co-item > .co-muted{ grid-area:qty; font-size:12.5px; font-weight:500; }
.co-item > b{ grid-area:total; font-family:'Fraunces',Georgia,serif; font-size:19px; font-weight:700; padding-right:4px; }
.co-sum{ display:flex; align-items:center; justify-content:space-between; gap:10px; padding:16px 20px; border-radius:20px; background:${INK}; color:${PAPER}; box-shadow:4px 4px 0 ${RED}; font-size:14px; font-weight:600; }
.co-sum b{ font-family:'Fraunces',Georgia,serif; font-size:30px; font-weight:700; line-height:1; }
.co-banner{ display:flex; align-items:center; gap:12px; padding:12px 16px; border-radius:18px; background:${PAPER}; border:1.5px solid ${RED}; color:${INK}; font-size:14px; font-weight:600; line-height:1.4; }
.co-banner svg{ flex-shrink:0; color:${RED}; }
.co-banner.wait{ border-color:${INK}; border-style:dashed; color:${MUTE}; }
.co-banner.wait svg{ color:${INK}; }
.co-actions{ display:flex; flex-wrap:wrap; align-items:center; gap:12px; padding-bottom:4px; }

/* "Mark as received" seal */
.co-page .co-verify{ display:inline-flex; align-items:center; gap:10px; height:50px; padding:0 22px 0 8px; border-radius:999px; border:2px solid ${INK};
  background:${RED}; color:${PAPER}; font-size:14px; font-weight:700; box-shadow:0 4px 0 ${RED_DEEP};
  transition:transform .15s, box-shadow .15s, background .2s; }
.co-page .co-verify:hover:not(:disabled){ transform:translateY(-2px); box-shadow:0 6px 0 ${RED_DEEP}; }
.co-page .co-verify:active:not(:disabled){ transform:translateY(3px); box-shadow:0 1px 0 ${RED_DEEP}; }
.co-page .co-verify.done{ background:${INK}; box-shadow:0 4px 0 ${RED}; opacity:1; cursor:default; }
.co-seal{ position:relative; width:34px; height:34px; display:flex; align-items:center; justify-content:center; flex-shrink:0; border-radius:50%; background:${PAPER}; color:${RED}; border:2px solid ${INK}; }
.co-verify.done .co-seal{ color:${INK}; animation:coPop .45s cubic-bezier(.2,.9,.3,1.4); }
@keyframes coPop{ 0%{ transform:scale(.5) rotate(-20deg); } 100%{ transform:scale(1) rotate(0); } }

/* confirm dialog */
.co-scrim{ position:fixed; inset:0; z-index:400; display:flex; align-items:center; justify-content:center; padding:20px; background:rgba(0,0,0,.55); animation:coFade .18s ease-out; }
.co-modal{ width:100%; max-width:400px; padding:28px 24px 24px; border-radius:28px; background:${PAPER}; border:2px solid ${INK}; box-shadow:7px 7px 0 ${RED}; animation:coPopIn .22s cubic-bezier(.2,.9,.3,1.2); }
.co-modal h3{ margin:0 0 8px; font-size:30px; font-style:italic; font-weight:700; color:${INK}; }
.co-modal p{ font-size:15px; line-height:23px; color:${MUTE}; }
.co-modal-actions{ display:grid; grid-template-columns:minmax(0,1fr) minmax(0,1fr); gap:12px; margin-top:24px; }
@keyframes coFade{ from{ opacity:0; } to{ opacity:1; } }
@keyframes coPopIn{ from{ opacity:0; transform:scale(.94) translateY(10px); } to{ opacity:1; transform:scale(1) translateY(0); } }

/* phones: price and status drop under the title so nothing is pushed off the right edge */
@media (max-width:480px){
  .co-page{ padding-left:12px; padding-right:12px; }
  .co-page .co-head{ flex-wrap:wrap; padding:12px; gap:10px 12px; }
  .co-thumb{ width:54px; height:54px; border-radius:15px; }
  .co-title{ font-size:18px; }
  .co-side{ flex-direction:row; align-items:center; justify-content:space-between; flex-basis:100%; }
  .co-side > b{ order:2; margin-left:auto; margin-right:8px; }
  .co-side .co-chev{ order:3; }
  .co-body{ padding:6px 12px 16px; }
  .co-sum b{ font-size:26px; }
  .co-actions .co-btn, .co-actions .co-verify{ flex:1 1 100%; }
}
@media (prefers-reduced-motion: reduce){ .co-page *{ transition:none !important; animation:none !important; } }
`;

type SK = "Pending" | "Packing" | "Shipped" | "Delivered" | "Cancelled" | "Refunded";

const STATUS_ICON: Record<SK, React.ReactNode> = {
  Pending: <Clock size={12} />,
  Packing: <Package size={12} />,
  Shipped: <Truck size={12} />,
  Delivered: <CheckCircle2 size={12} />,
  Cancelled: <Ban size={12} />,
  Refunded: <RotateCcw size={12} />,
};

const statusOf = (o: Order): SK => {
  if (o.isRefunded) return "Refunded";
  if (o.isCancelled) return "Cancelled";
  if (o.isDelivered || o.deliveryStatus?.buyerReceived) return "Delivered";
  if (o.deliveryStatus?.sellerDelivered || o.status === "shipped") return "Shipped";
  if (o.isPaid) return "Packing";
  return "Pending";
};

const money = (n?: number) => (!n || isNaN(n) ? "0.00" : Number(n).toFixed(2));
const when = (ms?: number | null) =>
  ms ? new Date(ms).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "-";

type Filter = "active" | "Delivered" | "Cancelled" | "all";

export default function CustomerOrders() {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { orders, status, error, actionLoading } = useSelector((s: RootState) => s.orders);

  const [filter, setFilter] = useState<Filter>("active");
  const [search, setSearch] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);
  const [copied, setCopied] = useState("");
  const [ask, setAsk] = useState<{ title: string; text: string; label: string; green?: boolean; run: () => void } | null>(null);

  useEffect(() => {
    const unsub = dispatch(listenToUserOrders());
    return () => unsub();
  }, [dispatch]);

  const count = (f: Filter) =>
    orders.filter((o) => {
      const s = statusOf(o);
      if (f === "all") return true;
      if (f === "active") return s === "Pending" || s === "Packing" || s === "Shipped";
      if (f === "Cancelled") return s === "Cancelled" || s === "Refunded";
      return s === f;
    }).length;

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return orders.filter((o) => {
      const s = statusOf(o);
      if (filter === "active" && !(s === "Pending" || s === "Packing" || s === "Shipped")) return false;
      if (filter === "Delivered" && s !== "Delivered") return false;
      if (filter === "Cancelled" && s !== "Cancelled" && s !== "Refunded") return false;
      if (!q) return true;
      return `${o.orderId} ${o.items.map((i) => i.name).join(" ")}`.toLowerCase().includes(q);
    });
  }, [orders, filter, search]);

  /* buyer half of delivery: only after the seller shipped (the slice enforces it too) */
  const received = (o: Order) =>
    setAsk({
      title: "Mark as received?",
      text: "Only do this once your parcel has arrived.",
      label: "Yes, received",
      green: true,
      run: () => dispatch(markOrderDelivered({ customerId: o.customerId, orderId: o.orderId, role: "customer" })),
    });
  const cancel = (o: Order) =>
    setAsk({
      title: "Cancel this order?",
      text: "The seller will be told and the order will be closed.",
      label: "Yes, cancel",
      run: () => dispatch(cancelOrder({ customerId: o.customerId, orderId: o.orderId })),
    });
  const copy = async (text: string) => {
    try { await navigator.clipboard.writeText(text); setCopied(text); setTimeout(() => setCopied(""), 1200); } catch { /* blocked */ }
  };

  const pills: { id: Filter; label: string; icon: React.ReactNode }[] = [
    { id: "active", label: "Active", icon: <Package size={14} /> },
    { id: "Delivered", label: "Delivered", icon: <CheckCircle2 size={14} /> },
    { id: "Cancelled", label: "Cancelled", icon: <Ban size={14} /> },
    { id: "all", label: "All", icon: <Clock size={14} /> },
  ];

  const activeCount = count("active");

  return (
    <div className="co-page">
      <style>{CSS}</style>

      <div className="co-wrap">
        <div className="co-top">
          <button type="button" className="co-back" onClick={() => navigate("/")}><ArrowLeft size={16} /> Back</button>
          <span className="co-mark">Bautables<i /></span>
        </div>

        <div className="co-head1">
          <h1 className="co-h1">My orders</h1>
          {activeCount > 0 && <span className="co-tag"><i /> {activeCount} on the way</span>}
        </div>

        <label className="co-search">
          <Search size={18} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search product or order no." />
        </label>

        <div className="co-pills">
          {pills.map((p) => (
            <button key={p.id} type="button" className={`co-pill${filter === p.id ? " on" : ""}`} onClick={() => setFilter(p.id)}>
              {p.icon} {p.label} <span className="co-n">{count(p.id)}</span>
            </button>
          ))}
        </div>

        {error && <p className="co-error">{error}</p>}

        {shown.length === 0 && status !== "failed" && (
          <div className="co-empty">
            <div className="co-bubble"><Package size={36} strokeWidth={1.6} /></div>
            <b>{orders.length === 0 ? "No orders yet" : "No orders match"}</b>
            <span className="co-muted">
              {orders.length === 0 ? "When you place an order, it shows up here." : "Clear the search or pick another filter."}
            </span>
            {orders.length === 0 && <button type="button" className="co-btn dark" style={{ marginTop: 8 }} onClick={() => navigate("/")}>Start shopping</button>}
          </div>
        )}

        {shown.map((o) => {
          const s = statusOf(o);
          const open = openId === o.orderId;
          const first = o.items[0];
          const sellerShipped = o.deliveryStatus?.sellerDelivered === true || o.status === "shipped";
          const live = !o.isCancelled && !o.isRefunded && !o.isDelivered && !o.deliveryStatus?.buyerReceived;
          const canReceive = live && sellerShipped;
          const canCancel = live && !sellerShipped;
          const qty = o.items.reduce((n, i) => n + i.quantity, 0);
          const ended = s === "Cancelled" || s === "Refunded";
          const paid = o.isPaid;
          const shippedDone = sellerShipped || s === "Delivered";
          const receivedDone = s === "Delivered";
          /* the step that is happening right now pulses red */
          const nowStep = receivedDone ? -1 : shippedDone ? 2 : paid ? 1 : 0;
          return (
            <div key={o.orderId} className={`co-glass co-card${open ? " open" : ""}${ended ? " faded" : ""}`}>
              <button type="button" className="co-head" onClick={() => setOpenId(open ? null : o.orderId)} aria-expanded={open}>
                <span className="co-thumb">{first?.photo ? <img src={first.photo} alt="" /> : <Package size={22} strokeWidth={1.5} />}</span>
                <span className="co-main">
                  <b className="co-title">
                    {first?.name || "Order"}
                    {o.items.length > 1 && <em>+{o.items.length - 1} more</em>}
                  </b>
                  <span className="co-sub">#{o.orderId.slice(0, 8).toUpperCase()} · {when(o.createdAt)} · {qty} item{qty === 1 ? "" : "s"}</span>
                </span>
                <span className="co-side">
                  <span className={`co-badge co-${s.toLowerCase()}`}>{STATUS_ICON[s]} {s}</span>
                  <b>€{money(o.total)}</b>
                  <ChevronDown size={18} className="co-chev" />
                </span>
              </button>

              {open && (
                <div className="co-body">
                  {!ended && (
                    <div className="co-track" aria-label="Delivery progress">
                      <div className={`co-step${paid ? " done" : ""}${nowStep === 0 ? " now" : ""}`}><span className="co-dot">{paid ? <Check size={15} strokeWidth={3.2} /> : null}</span>Paid</div>
                      <div className={`co-step${shippedDone ? " done" : ""}${nowStep === 1 ? " now" : ""}`}><span className="co-dot">{shippedDone ? <Truck size={14} strokeWidth={2.6} /> : nowStep === 1 ? <Package size={14} strokeWidth={2.6} /> : null}</span>Shipped</div>
                      <div className={`co-step${receivedDone ? " done" : ""}${nowStep === 2 ? " now" : ""}`}><span className="co-dot">{receivedDone ? <PackageCheck size={15} strokeWidth={2.6} /> : nowStep === 2 ? <Truck size={14} strokeWidth={2.6} /> : null}</span>Received</div>
                    </div>
                  )}

                  {canReceive && (
                    <div className="co-banner"><Truck size={20} /> Your order is on its way. Tap "Mark as received" when it arrives.</div>
                  )}
                  {canCancel && paid && (
                    <div className="co-banner wait"><Clock size={20} /> Waiting for the seller to ship. You can cancel until it ships.</div>
                  )}

                  <div className="co-items">
                    {o.items.map((i, n) => (
                      <div key={i.productId + n} className="co-item">
                        <span className="co-iphoto">{i.photo ? <img src={i.photo} alt="" /> : <Package size={18} strokeWidth={1.5} />}</span>
                        <span className="co-iname">{i.name}</span>
                        <span className="co-muted">{i.quantity} × €{money(i.price)}</span>
                        <b>€{money(i.price * i.quantity)}</b>
                      </div>
                    ))}
                  </div>

                  <div className="co-sum"><span>Total</span><b>€{money(o.total)}</b></div>
                  {o.paymentProvider && <p className="co-sub">Paid with {o.paymentProvider}{o.currency ? ` (${String(o.currency).toUpperCase()})` : ""}</p>}
                  {o.cancelReason && <p className="co-sub">Reason: {o.cancelReason}</p>}
                  {o.refundReason && <p className="co-sub">Refund note: {o.refundReason}</p>}

                  <div className="co-actions">
                    {(canReceive || s === "Delivered") && (
                      <button
                        type="button"
                        className={`co-verify${s === "Delivered" ? " done" : ""}`}
                        disabled={actionLoading || s === "Delivered"}
                        aria-label={s === "Delivered" ? "Order received" : "Mark as received"}
                        onClick={() => received(o)}
                      >
                        <span className="co-seal"><Check size={18} strokeWidth={3.4} /></span>
                        {s === "Delivered" ? "Received" : "Mark as received"}
                      </button>
                    )}
                    {canCancel && (
                      <button type="button" className="co-btn ghost" disabled={actionLoading} onClick={() => cancel(o)}>
                        <Ban size={15} /> Cancel order
                      </button>
                    )}
                    <button type="button" className="co-btn ghost" onClick={() => copy(o.orderId)}>
                      {copied === o.orderId ? <Check size={15} /> : <Copy size={15} />} Order ID
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {ask && (
        <div className="co-scrim" onClick={() => setAsk(null)}>
          <div className="co-modal" role="dialog" aria-modal="true" aria-label={ask.title} onClick={(e) => e.stopPropagation()}>
            <h3>{ask.title}</h3>
            <p>{ask.text}</p>
            <div className="co-modal-actions">
              <button type="button" className="co-btn ghost" onClick={() => setAsk(null)}>Go back</button>
              <button type="button" className={`co-btn${ask.green ? " dark" : ""}`} onClick={() => { ask.run(); setAsk(null); }}>{ask.label}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}